import { useEffect } from 'react';

import useInterval from '@use-it/interval';

import { INTERVAL_TIME } from '../../constants';
import { useBrowserInteractionTime } from '../use-browser-interaction-time';
import { useHeartbeatService } from '../use-heartbeat-service';

export const useHeartbeatOnInterval = (
	endpoint: string | undefined,
	idleTime: number,
	onAuthenticationFailed?: () => void,
	onBadRequest?: () => void,
): {
	nextCallTimestamp: void | Date;
	sessionExpiryTimestamp: void | Date;
	active: boolean;
	tabActive: boolean;
	deactivated: boolean;
} => {
	const {
		nextCallTimestamp,
		sessionExpiryTimestamp,
		callHeartbeatService,
		setCallHeartbeatService,
		deactivated,
	} = useHeartbeatService(endpoint, onAuthenticationFailed, onBadRequest);
	const { active, tabActive } = useBrowserInteractionTime(idleTime);

	/**
	 * Initial heartbeat api request
	 */
	useEffect(() => {
		setCallHeartbeatService(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Call heartbeat api when user is idle and the tab is active
	 */
	useEffect(() => {
		if (!active && tabActive) {
			setCallHeartbeatService(true);
		}
	}, [active, tabActive, setCallHeartbeatService]);

	useInterval(() => {
		if (callHeartbeatService) {
			return;
		}

		/**
		 * Call heartbeat api when session has expired to initiate redirect.
		 */
		if (sessionExpiryTimestamp && sessionExpiryTimestamp.getTime() <= Date.now()) {
			setCallHeartbeatService(true);
			return;
		}

		/**
		 * Call heartbeat api if user is active and nextCallTimestamp is reached.
		 */
		if (active && tabActive && nextCallTimestamp && nextCallTimestamp.getTime() <= Date.now()) {
			setCallHeartbeatService(true);
		}
	}, INTERVAL_TIME);

	return {
		nextCallTimestamp,
		sessionExpiryTimestamp,
		active,
		tabActive,
		deactivated,
	};
};
